import { api, type QueryParams } from "@/lib/api/client";

export type DocumentStatus = "draft" | "sent" | "completed" | "cancelled";
export type SignerStatus = "pending" | "signed" | "declined";
export type FieldType = "signature" | "initials" | "date";

export interface SignatureField {
  id: string;
  signer_id: string;
  signer_user_id: string | null;
  name: string;
  field_type: FieldType;
  page_number: number;
  x: string;
  y: string;
  width: string;
  height: string;
  required: boolean;
  sort_order: number;
  signed_at: string | null;
}

export interface DocumentSigner {
  id: string;
  user_id: string;
  employee_name: string | null;
  employee_number: string | null;
  status: SignerStatus;
  signed_at: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  last_viewed_at: string | null;
  last_reminded_at: string | null;
  field_count: number;
  /** Their place in the order of signing; all 0 when everybody signs at once. */
  sign_order: number;
}

export interface PageSize {
  w: number;
  h: number;
  ox?: number;
  oy?: number;
}

export interface SignatureDocument {
  id: string;
  title: string;
  description: string | null;
  page_count: number;
  page_sizes: PageSize[] | null;
  status: DocumentStatus;
  due_date: string | null;
  original_file_id: string | null;
  working_file_id: string | null;
  signed_file_id: string | null;
  final_sha256: string | null;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
  signers: DocumentSigner[];
  fields: SignatureField[];
  signed_count: number;
  /** Refusals leave the document sent, so this is how the page tells them apart. */
  declined_count: number;
  /** Set when it was written in the portal: its versions and editing live there. */
  written_document_id: string | null;
}

export interface DocumentListItem {
  id: string;
  title: string;
  status: DocumentStatus;
  due_date: string | null;
  page_count: number;
  sent_at: string | null;
  completed_at: string | null;
  created_at: string;
  signer_count: number;
  signed_count: number;
  declined_count: number;
  /** The earliest refusal, so the list can name it without a click. */
  first_declined_by: string | null;
  first_declined_at: string | null;
  /** Everybody a sent document is still waiting on, refusals first, then longest waiting. */
  waiting_on: WaitingSigner[];
}

/** Somebody a sent document is still waiting on, and since when. */
export interface WaitingSigner {
  user_id: string;
  employee_name: string | null;
  employee_number: string | null;
  status: "pending" | "declined";
  /** The day it was sent to them, or the day they said no. */
  since: string | null;
  days_waiting: number | null;
  decline_reason: string | null;
  last_viewed_at: string | null;
}

export interface MyDocument {
  document_id: string;
  title: string;
  description: string | null;
  status: DocumentStatus;
  due_date: string | null;
  my_status: SignerStatus;
  my_field_count: number;
  sent_at: string | null;
  completed_at: string | null;
  signed_file_id: string | null;
}

export interface SignatureEvent {
  id: number;
  signer_id: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  event_type: string;
  ip: string | null;
  created_at: string;
  event_metadata: Record<string, unknown> | null;
}

export interface FieldInput {
  signer_user_id: string;
  name?: string;
  field_type?: FieldType;
  page_number: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required?: boolean;
}

export interface Group {
  id: string;
  name: string;
  description: string | null;
  /** Set on a group that follows a position; its members are not edited by hand. */
  position_id: string | null;
  member_count: number;
  /** Messages in the group's chat, for the warning before it is deleted. */
  message_count?: number;
}

export const documentKeys = {
  all: ["documents"] as const,
  list: (filters: QueryParams) => [...documentKeys.all, "list", filters] as const,
  detail: (id: string) => [...documentKeys.all, "detail", id] as const,
  events: (id: string) => [...documentKeys.all, "events", id] as const,
  mine: () => [...documentKeys.all, "mine"] as const,
  mineDetail: (id: string) => [...documentKeys.all, "mine", id] as const,
  groups: () => ["groups"] as const,
};

export const documentApi = {
  list: (filters: QueryParams = {}) =>
    api.get<DocumentListItem[]>("/documents", { query: filters }),

  detail: (id: string) => api.get<SignatureDocument>(`/documents/${id}`),

  events: (id: string) => api.get<SignatureEvent[]>(`/documents/${id}/events`),

  create: (file: File, title: string, description?: string, dueDate?: string) => {
    const body = new FormData();
    body.append("file", file);
    body.append("title", title);
    if (description) body.append("description", description);
    if (dueDate) body.append("due_date", dueDate);
    return api.post<SignatureDocument>("/documents", body);
  },

  setSigners: (id: string, user_ids: string[], group_ids: string[] = []) =>
    api.put<SignatureDocument>(`/documents/${id}/signers`, { user_ids, group_ids }),

  setFields: (id: string, fields: FieldInput[]) =>
    api.put<SignatureDocument>(`/documents/${id}/fields`, { fields }),

  send: (id: string) => api.post<SignatureDocument>(`/documents/${id}/send`),

  remind: (id: string) => api.post<{ message: string }>(`/documents/${id}/remind`),

  cancel: (id: string, reason?: string) =>
    api.post<SignatureDocument>(`/documents/${id}/cancel`, { reason: reason ?? null }),

  mine: () => api.get<MyDocument[]>("/me/documents"),

  mineDetail: (id: string) => api.get<SignatureDocument>(`/me/documents/${id}`),

  sign: (id: string, payload: { mode: "drawn" | "profile"; data_url?: string; save_to_profile?: boolean }) =>
    api.post<SignatureDocument>(`/me/documents/${id}/sign`, payload),

  decline: (id: string, reason: string) =>
    api.post<{ message: string }>(`/me/documents/${id}/decline`, { reason }),

  groups: () => api.get<Group[]>("/groups"),

  createGroup: (name: string, description?: string) =>
    api.post<Group>("/groups", { name, description: description ?? null }),

  groupMembers: (id: string) =>
    api.get<{ user_id: string; full_name_en: string; employee_id: string | null }[]>(
      `/groups/${id}/members`,
    ),

  setGroupMembers: (id: string, user_ids: string[]) =>
    api.put<Group>(`/groups/${id}/members`, { user_ids }),

  group: (id: string) => api.get<Group>(`/groups/${id}`),
  deleteGroup: (id: string) => api.delete<{ message: string }>(`/groups/${id}`),
};
